import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, FileText } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import * as ozon from "@/api/ozon_fbs";
import { apiErrorText, LoadState, useGoBack } from "@/lib/scan-ui/screen";
import { PtmoSkippedNote } from "@/pages/fbs/PtmoSkippedNote";

/**
 * Завершение поставки Ozon — ОТДЕЛЬНЫЙ экран, как у ВБ.
 *
 * ⚠️ Не блок на скане. Скан-экран живёт в один вьюпорт без скролла, и место на
 * нём принадлежит тому, ради чего кладовщик его открыл, — списку к сбору. А
 * здесь разбор и необратимое действие: один комплект документов 1С на всю
 * поставку. Их нельзя ставить на пути у человека, который сканирует.
 */
export function OzonFinishPage(): React.ReactElement {
  const { supplyId = "" } = useParams();
  const goBack = useGoBack("/ozon");
  const qc = useQueryClient();

  const supplyQ = useQuery({
    queryKey: ["ozon", "supply", supplyId],
    queryFn: () => ozon.getSupply(supplyId),
    enabled: !!supplyId,
  });
  const supply = supplyQ.data;

  const docsMut = useMutation({
    mutationFn: () => ozon.createSupplyDocuments(supplyId),
    onSuccess: (updated) => {
      toast.success(
        `Документы созданы: отправлений ${updated.postings_total}, ` +
          `документов ${updated.documents.length}`,
      );
      qc.setQueryData(["ozon", "supply", supplyId], updated);
      void qc.invalidateQueries({ queryKey: ["ozon", "pick-state"] });
      void qc.invalidateQueries({ queryKey: ["ozon", "issues"] });
    },
    // ⚠️ Долгий тост: отказ 1С — это текст, с которым идут разбираться в учёт
    // (не хватило остатка, не заполнена ссылка). Исчезнув за три секунды, он
    // заставил бы повторять необратимое действие, чтобы прочитать причину.
    onError: (e) => toast.error(apiErrorText(e, "Не удалось выписать документы"), {
      duration: 20000,
    }),
  });

  const settled = !!supply?.settled_at;
  const blocked = (supply?.blockers.length ?? 0) > 0;

  return (
    <div className="space-y-4">
      {/* Шапка — всегда, до и вместо данных: иначе при ошибке загрузки экран
          остаётся без единственной кнопки выхода. */}
      <div className="flex items-center gap-2">
        <button type="button" onClick={goBack} aria-label="Назад">
          <ArrowLeft className="size-5" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold">Завершение поставки</h1>
          {supply && (
            <p className="text-sm text-muted-foreground">
              Ozon · отправлений {supply.postings_total} · штук {supply.units_total}
              {settled && " · документы выписаны"}
            </p>
          )}
        </div>
      </div>

      {!supply ? (
        <LoadState
          isLoading={supplyQ.isLoading}
          error={supplyQ.error}
          onRetry={() => void supplyQ.refetch()}
          className="flex flex-col items-center justify-center gap-3 rounded-xl border p-6 text-center"
        />
      ) : (
        <>
          {blocked && (
            <Card className="border-rose-300">
              <CardHeader>
                <CardTitle className="text-lg text-rose-700">
                  <AlertTriangle className="mr-1 inline size-5" />
                  Нельзя выписать — разберите
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                {/* Причины считает СЕРВЕР тем же правилом, что и сама цепочка:
                    вторая их копия здесь разъехалась бы молча. */}
                {supply.blockers.map((b) => (
                  <div key={b} className="rounded border border-rose-200 p-2">
                    {b}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Состав поставки</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>
                Собрано отправлений: <b>{supply.postings_total}</b>, в них штук:{" "}
                <b>{supply.units_total}</b>.
              </p>
              <p className="text-xs text-muted-foreground">
                Поставка — это отправления, которые собрали и увезут за раз. Документы
                1С выписываются на неё целиком: один комплект, а не по комплекту на
                каждую коробку.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                <FileText className="mr-1 inline size-5" />
                Документы 1С
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {supply.documents.length > 0 ? (
                <ul className="space-y-1">
                  {supply.documents.map((d) => (
                    <li key={d.kind} className="rounded border p-2">
                      {d.number || d.kind}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">Ещё не выписаны.</p>
              )}
              <PtmoSkippedNote skipped={supply.ptmo_skipped} />
              <p className="text-xs text-muted-foreground">
                Передача собственности, счёт-фактура, заказ покупателя и расходная.
                Повтор после сбоя доводит начатое, а не создаёт вторые: ссылки
                записываются сразу, до проведения.
              </p>
              <Button
                className="w-full"
                disabled={docsMut.isPending || blocked || settled}
                onClick={() => docsMut.mutate()}
              >
                {settled ? "Поставка закрыта" : "Закрыть поставку и выписать документы"}
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
